import { Pencil } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Card, Col, Nav, Row, Table } from 'react-bootstrap'
import Loader from '../../../components/atoms/Loader'
import { ActiveInActive } from '../../../components/atoms/StatusLabel'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import ModalAddVehicle from '../../../components/modal/vehicle/ModalAddVehicle'
import ModalUpdateVehicle from '../../../components/modal/vehicle/ModalUpdateVehicle'
import FilePreviewModal from '../../../components/modal/common/FilePreviewModal'
import api from '../../../config/AxiosInstance'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'
import { formatThousandSeparator } from '../../../utils/TextFormatter'

export default function MasterVehicle() {
  const headerTitleStyle = {
    color: 'var(--primary-orange)',
    fontSize: '24px',
    fontWeight: '600'
  }

  const tableHeaderStyle = {
    backgroundColor: '#f8f9fa',
    color: '#333333',
    fontWeight: '700',
    borderBottom: '1px solid #e9ecef',
    textTransform: 'uppercase',
    fontSize: '0.85rem'
  }

  const [showModalAdd, setShowModalAdd] = useState(false)
  const [showModalUpdate, setShowModalUpdate] = useState(false)
  const [isAdd, setIsAdd] = useState(false)
  const [isUpdate, setIsUpdate] = useState(false)
  const [vehicles, setVehicles] = useState([])
  const [activeKey, setActiveKey] = useState('internal')
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const [editId, setEditId] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const [showPreview, setShowPreview] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')
  const [previewName, setPreviewName] = useState('')
  const [previewType, setPreviewType] = useState('')
  const { per_page, current_page } = meta

  const prevActiveKey = useRef(activeKey)
  const abortControllerRef = useRef(null)

  useEffect(() => {
    if (prevActiveKey.current !== activeKey) {
      prevActiveKey.current = activeKey
      if (current_page !== 1) {
        setMeta((prev) => ({ ...prev, current_page: 1 }))
        return
      }
    }

    fetchVehicles()

    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdd, isUpdate, per_page, current_page, activeKey])

  function handleOnChangeMeta(e) {
    const perPageValue = parseInt(e.target.value, 10)
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: perPageValue,
      current_page: 1
    }))
    window.scrollTo(0, 0)
  }

  const handlePageChange = (data) => {
    const selectedPage = data.selected + 1
    setMeta((prevMeta) => ({ ...prevMeta, current_page: selectedPage }))
    window.scrollTo(0, 0)
  }

  function handleEdit(id) {
    setShowModalUpdate((show) => !show)
    setEditId(id)
  }

  const handleShowPreview = (url, name, type = '') => {
    if (!url) return
    setPreviewUrl(url)
    setPreviewName(name || 'File Preview')
    setPreviewType(type)
    setShowPreview(true)
  }

  async function fetchVehicles() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setIsLoading(true)
    try {
      const res = await api.get('master/vehicles', {
        signal,
        params: { per_page, page: current_page, type: activeKey }
      })
      if (res.data.success) {
        const { metadata, data } = res.data
        setVehicles(data)
        setMeta(metadata)
      }
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setIsLoading(false)
      }
    }
  }

  const handleSelectActive = (key) => {
    setActiveKey(key)
  }

  return (
    <div className="container-fluid px-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Vehicle</h3>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Nav
            variant="underline"
            onSelect={handleSelectActive}
            defaultActiveKey={activeKey}
            className="border-bottom"
          >
            <Nav.Item>
              <Nav.Link
                eventKey={'internal'}
                className={`text-uppercase fw-bold ${
                  activeKey === 'internal'
                    ? 'text-primary-orange'
                    : 'text-muted'
                }`}
              >
                Internal
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey={'external'}
                className={`text-uppercase fw-bold ${
                  activeKey === 'external'
                    ? 'text-primary-orange'
                    : 'text-muted'
                }`}
              >
                External
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Col>
      </Row>

      <Row className="align-items-center mb-3">
        <Col>
          <h4
            className="text-muted text-uppercase mb-0"
            style={{ fontSize: '14px' }}
          >
            Vehicle List
          </h4>
        </Col>
        <Col className="text-end">
          <button
            className="btn btn-primary-orange shadow-sm rounded px-4"
            onClick={() => setShowModalAdd(true)}
          >
            Add Vehicle
          </button>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
            <Table responsive hover className="mb-0 b-table-frameless">
              <thead style={tableHeaderStyle}>
                <tr>
                  <th className="p-2 align-middle">Plate No</th>
                  <th className="p-2 align-middle">Vehicle Type</th>
                  <th className="p-2 align-middle">Capacity(Kg)</th>
                  <th className="p-2 align-middle">Panjang</th>
                  <th className="p-2 align-middle">Lebar</th>
                  <th className="p-2 align-middle">Tinggi</th>
                  <th className="p-2 align-middle">Volume(CM³)</th>
                  {activeKey === 'external' && (
                    <th className="p-2 align-middle">STNK</th>
                  )}
                  <th className="p-2 align-middle">Status</th>
                  <th className="p-2 align-middle text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td
                      colSpan={activeKey === 'external' ? 10 : 9}
                      className="text-center p-5"
                    >
                      <Loader />
                    </td>
                  </tr>
                )}
                {!isLoading && !vehicles.length && (
                  <tr>
                    <td
                      colSpan={activeKey === 'external' ? 10 : 9}
                      className="text-center p-5"
                    >
                      <div className="text-muted">No data available</div>
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  vehicles.length > 0 &&
                  vehicles?.map((item, i) => (
                    <TbodyRow
                      data={item}
                      key={item.id}
                      handleEdit={() => handleEdit(item.id)}
                      activeKey={activeKey}
                      handleShowPreview={handleShowPreview}
                    />
                  ))}
              </tbody>
            </Table>
            {vehicles.length > 0 && (
              <div className="p-3 border-top bg-white">
                <TablePagination
                  meta={meta}
                  handleOnChangeMeta={handleOnChangeMeta}
                  handlePageChange={handlePageChange}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <ModalAddVehicle
        show={showModalAdd}
        onHide={() => setShowModalAdd(false)}
        setIsAdd={setIsAdd}
        activeKey={activeKey}
      />
      <ModalUpdateVehicle
        show={showModalUpdate}
        onHide={() => {
          setShowModalUpdate(false)
        }}
        data={editId}
        setIsUpdate={setIsUpdate}
        activeKey={activeKey}
      />
      <FilePreviewModal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        fileUrl={previewUrl}
        fileName={previewName}
        fileType={previewType}
      />
    </div>
  )
}

function TbodyRow({ data, handleEdit, activeKey, handleShowPreview }) {
  return (
    <tr key={data.id}>
      <td className="p-2 align-middle fw-medium text-dark">{data.plate_no}</td>
      <td className="p-2 align-middle">{data.vehicle_type}</td>
      <td className="p-2 align-middle fw-bold">
        {formatThousandSeparator(data.capacity)}
      </td>
      <td className="p-2 align-middle text-muted">{data.length ?? '-'}</td>
      <td className="p-2 align-middle text-muted">{data.width ?? '-'}</td>
      <td className="p-2 align-middle text-muted">{data.height ?? '-'}</td>
      <td className="p-2 align-middle fw-bold">
        {formatThousandSeparator(data.volume)}
      </td>
      {activeKey === 'external' && (
        <td className="p-2 align-middle">
          {data.stnk_doc ? (
            <span
              className="text-primary cursor-pointer text-decoration-underline"
              onClick={() =>
                handleShowPreview(data.stnk_doc, `STNK - ${data.plate_no}`)
              }
              style={{ cursor: 'pointer' }}
            >
              {data.stnk_doc ? data.stnk_doc.split('/').pop() : '-'}
            </span>
          ) : (
            '-'
          )}
        </td>
      )}
      <td className="p-2 align-middle">
        <ActiveInActive text={data.status ? 'active' : 'inactive'} />
      </td>
      <td className="p-2 align-middle text-end">
        <TableAction>
          <button
            className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0"
            style={{ width: '32px', height: '32px' }}
            onClick={() => handleEdit(data.id)}
          >
            <Pencil size={13} />
          </button>
        </TableAction>
      </td>
    </tr>
  )
}
